import React, { useEffect, useState } from 'react';
import { Bot, Brain, Coins, Ear, FileText, MessageSquare, Network, RefreshCw, Sparkles, Waypoints } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  bajo: 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  medio: 'border border-sky-400/20 bg-sky-500/10 text-sky-200',
  alto: 'border border-rose-400/20 bg-rose-500/10 text-rose-200',
};

export default function AynaraCommandCenterHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  const loadHub = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/admin/aynara-command-center`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar Aynara Command Center');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar Aynara Command Center');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHub(); }, []);

  const sendTestAlert = async () => {
    try {
      setSendingAlert(true);
      const res = await authFetch(`${API}/admin/aynara/whatsapp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Prueba de Aynara CEO',
          message: 'Este es un mensaje de prueba desde el Command Center para validar la conexión ejecutiva.',
          alert_type: 'manual_exec_alert',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo preparar el mensaje de WhatsApp');
      toast.success(data.alert?.status === 'sent' ? 'WhatsApp ejecutivo enviado' : 'Mensaje preparado; faltan credenciales o validación externa');
      await loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo preparar el mensaje de WhatsApp');
    } finally {
      setSendingAlert(false);
    }
  };

  const sendDailySummary = async () => {
    try {
      setSendingSummary(true);
      const res = await authFetch(`${API}/admin/aynara/whatsapp/daily-summary`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo preparar el resumen ejecutivo');
      toast.success(data.alert?.status === 'sent' ? 'Resumen ejecutivo enviado' : 'Resumen preparado; falta cierre de WhatsApp Cloud');
      await loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo preparar el resumen ejecutivo');
    } finally {
      setSendingSummary(false);
    }
  };

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="aynara-command-center-loading">Cargando Aynara Command Center...</div>;

  return (
    <div className="space-y-6" data-testid="aynara-command-center-hub">
      <div className="glass-card tracing-panel overflow-hidden border-primary/20 bg-[radial-gradient(circle_at_50%_22%,rgba(59,130,246,0.22),transparent_28%),linear-gradient(180deg,rgba(4,10,24,0.98),rgba(2,6,23,0.98))]" data-testid="aynara-command-center-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3 border-white/10 bg-white/5 text-slate-200">Aynara Command Center</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">MCP-style · memoria operativa · demo mode · router central</p>
            <h3 className="mt-3 text-3xl font-semibold text-white">Aynara escucha, enruta, recuerda y ejecuta sobre todo el ecosistema Neuralia.</h3>
            <p className="mt-4 max-w-3xl text-sm text-slate-200/82">Esta es la pantalla central que faltaba: aquí se ve qué quiere el usuario, qué agente toma el liderazgo, qué stack recomienda el router, qué memoria se guarda y qué demo se enseña en reunión.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="aynara-command-center-chips">
              {['Listening', 'Router', 'Memoria 369', 'Demo Mode', 'Créditos', 'LLOPS'].map((item) => <span key={item} className="insight-chip border-white/10 bg-white/5 text-slate-100">{item}</span>)}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="glass-card p-5 bg-white/5 border-white/10" data-testid="aynara-command-center-metric-memory"><p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Memorias</p><p className="mt-3 text-3xl font-bold text-white">{hub.metrics.memory_entries}</p><p className="mt-2 text-xs text-slate-300">contextos guardados</p></div>
            <div className="glass-card p-5 bg-white/5 border-white/10" data-testid="aynara-command-center-metric-routes"><p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Routings</p><p className="mt-3 text-3xl font-bold text-white">{hub.metrics.recent_routings}</p><p className="mt-2 text-xs text-slate-300">muestras recientes</p></div>
            <div className="glass-card p-5 bg-white/5 border-white/10" data-testid="aynara-command-center-metric-agents"><p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Agentes</p><p className="mt-3 text-3xl font-bold text-white">{hub.metrics.available_agents}</p><p className="mt-2 text-xs text-slate-300">familia simple Neuralia</p></div>
            <div className="glass-card p-5 bg-white/5 border-white/10" data-testid="aynara-command-center-metric-credits"><p className="text-[10px] uppercase tracking-[0.18em] text-slate-300">Packs</p><p className="mt-3 text-3xl font-bold text-white">{hub.metrics.credit_packs}</p><p className="mt-2 text-xs text-slate-300">créditos activos</p></div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={loadHub} className="btn-secondary" disabled={loading} data-testid="aynara-command-center-refresh-button">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="glass-card tracing-panel p-6" data-testid="aynara-command-center-router-card">
          <div className="flex items-center gap-2 text-text-heading"><Waypoints className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Neuralia Router</h4></div>
          <div className="mt-4 space-y-3">
            {hub.router_policies.map((item) => (
              <div key={item.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`aynara-router-policy-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.primary_agent}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.intent}</p>
                  </div>
                  <div className="flex gap-2">
                    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS[item.cost_profile]}`}>coste {item.cost_profile}</span>
                    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS[item.risk_profile]}`}>riesgo {item.risk_profile}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">{item.recommended_stack.map((stack) => <span key={stack} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{stack}</span>)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card tracing-panel p-6" data-testid="aynara-command-center-demo-card">
          <div className="flex items-center gap-2 text-text-heading"><Ear className="w-5 h-5 text-primary animate-pulse" /><h4 className="text-lg font-semibold">Demo Mode · modo reunión</h4></div>
          <p className="mt-3 text-sm text-text-body">{hub.demo_mode.meeting_goal}</p>
          <div className="mt-4 rounded-[22px] border border-primary/15 bg-primary/5 p-4" data-testid="aynara-command-center-demo-order-card">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Orden recomendado</p>
            <div className="mt-3 flex flex-wrap gap-2">{hub.demo_mode.recommended_order.map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-primary">{item}</span>)}</div>
          </div>
          <div className="mt-4 space-y-3">
            {hub.demo_mode.scenarios.map((item) => (
              <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`aynara-demo-scenario-${item.id}`}>
                <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                <p className="mt-1 text-xs text-text-muted">{item.persona} · {item.company_type}</p>
                <p className="mt-3 text-sm text-text-body">{item.desired_outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <div className="glass-card p-6" data-testid="aynara-command-center-memory-card">
          <div className="flex items-center gap-2 text-text-heading"><Brain className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Memoria Operativa 369</h4></div>
          <div className="mt-4 space-y-3">
            {hub.recent_memory.map((item) => (
              <div key={item.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`aynara-memory-item-${item.id}`}>
                <p className="text-sm font-semibold text-text-heading">{item.objective}</p>
                <p className="mt-2 text-sm text-text-body">{item.summary}</p>
                <div className="mt-3 flex flex-wrap gap-2">{(item.tags || []).map((tag, index) => <span key={`${tag}-${index}`} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{tag}</span>)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="aynara-command-center-route-samples-card">
            <div className="flex items-center gap-2 text-text-heading"><Network className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Últimos enroutamientos</h4></div>
            <div className="mt-4 space-y-3">
              {hub.router_samples.map((item) => (
                <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`aynara-route-sample-${item.id}`}>
                  <p className="text-sm font-semibold text-text-heading">{item.router.primary_agent}</p>
                  <p className="mt-2 text-sm text-text-body">{item.message}</p>
                  <div className="mt-3 flex flex-wrap gap-2">{item.router.recommended_stack.map((stack) => <span key={stack} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{stack}</span>)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="aynara-command-center-credit-card">
            <div className="flex items-center gap-2 text-text-heading"><Coins className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Venta de créditos integrada</h4></div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {hub.credit_packs.map((item) => (
                <div key={item.id} className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid={`aynara-credit-pack-${item.id}`}>
                  <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                  <p className="mt-1 text-xs text-text-muted">{item.credits} créditos</p>
                  <p className="mt-3 text-2xl font-bold text-primary">${item.price}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-6" data-testid="aynara-command-center-activation-card">
            <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Activación paso a paso</h4></div>
            <div className="mt-4 rounded-[22px] border border-primary/15 bg-primary/5 p-4" data-testid="aynara-command-center-activation-metrics">
              <p className="text-sm text-text-body">Configuradas: <span className="text-text-heading font-semibold">{hub.activation_readiness.configured_count}</span> · Faltan: <span className="text-text-heading font-semibold">{hub.activation_readiness.missing_count}</span></p>
            </div>
            <div className="mt-4 space-y-3">
              {hub.activation_readiness.env_status.map((item) => (
                <div key={item.key} className="rounded-[18px] border border-border-subtle bg-background/55 px-4 py-3 flex items-center justify-between gap-3" data-testid={`aynara-command-center-env-${item.key}`}>
                  <span className="text-sm text-text-heading">{item.key}</span>
                  <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${item.is_configured ? 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>{item.status}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2 text-sm text-text-body" data-testid="aynara-command-center-activation-steps">
              {hub.activation_readiness.steps.map((item, index) => <p key={item}>{index + 1}. {item}</p>)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <div className="glass-card p-6" data-testid="aynara-command-center-whatsapp-card">
          <div className="flex items-center gap-2 text-text-heading"><MessageSquare className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">CEO WhatsApp</h4></div>
          <p className="mt-3 text-sm text-text-body">Aynara ya puede preparar alertas, órdenes y resúmenes ejecutivos para el número personal del CEO.</p>
          <div className="mt-4 rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="aynara-command-center-whatsapp-readiness">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado</p>
            <p className="mt-2 text-sm text-text-heading">{hub.ceo_whatsapp?.configured ? `Conectado a ${hub.ceo_whatsapp?.recipient}` : hub.ceo_whatsapp?.next_step}</p>
            {!!hub.ceo_whatsapp?.missing_credentials?.length && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="aynara-command-center-whatsapp-missing">
                {hub.ceo_whatsapp.missing_credentials.map((item) => <span key={item} className="rounded-full border border-amber-300/20 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-amber-100">{item}</span>)}
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="aynara-command-center-whatsapp-metrics">
            <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Total</p><p className="mt-2 text-2xl font-semibold text-text-heading">{hub.ceo_whatsapp?.metrics?.total || 0}</p></div>
            <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Enviados</p><p className="mt-2 text-2xl font-semibold text-text-heading">{hub.ceo_whatsapp?.metrics?.sent || 0}</p></div>
            <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pendientes</p><p className="mt-2 text-2xl font-semibold text-text-heading">{hub.ceo_whatsapp?.metrics?.pending || 0}</p></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={sendTestAlert} className="btn-secondary" disabled={sendingAlert} data-testid="aynara-command-center-whatsapp-test-button">{sendingAlert ? 'Preparando...' : 'Enviar prueba CEO'}</button>
            <button onClick={sendDailySummary} className="btn-secondary" disabled={sendingSummary} data-testid="aynara-command-center-whatsapp-summary-button">{sendingSummary ? 'Preparando...' : 'Enviar resumen diario'}</button>
          </div>
        </div>

        <div className="glass-card p-6" data-testid="aynara-command-center-neuraliatesix-card">
          <div className="flex items-center gap-2 text-text-heading"><FileText className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Neuralia369Tesix</h4></div>
          <p className="mt-3 text-sm text-text-body">Nuevo servicio académico con LLOPS para análisis, metodología, fuentes, reescritura y paquete final de tesis.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="aynara-command-center-neuraliatesix-metrics">
            <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Proyectos</p><p className="mt-2 text-2xl font-semibold text-text-heading">{hub.neuraliatesix?.metrics?.projects || 0}</p></div>
            <div className="rounded-[18px] border border-border-subtle bg-card/40 p-4"><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">AI risk medio</p><p className="mt-2 text-2xl font-semibold text-text-heading">{hub.neuraliatesix?.metrics?.avg_ai_risk || 0}</p></div>
          </div>
          <div className="mt-4 space-y-3" data-testid="aynara-command-center-neuraliatesix-flow">
            {(hub.neuraliatesix?.delivery_flow || []).map((item) => (
              <div key={item} className="rounded-[18px] border border-border-subtle bg-background/55 px-4 py-3 text-sm text-text-body">• {item}</div>
            ))}
          </div>
          <a href="/neuralia369tesix" className="mt-4 inline-flex text-sm text-primary" data-testid="aynara-command-center-neuraliatesix-link">Abrir dashboard separado</a>
        </div>
      </div>
    </div>
  );
}